/**
 * Folder-to-unit mapping row with auto-detection suggestions and manual override.
 */
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Check, ChevronDown, FolderOpen, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PropertyUnit } from '@/lib/units';
import type { FolderMappingSuggestion } from '@/lib/admin/gallery-mapping';
import { normalizeFolderNameForStorage } from '@/lib/admin/gallery-mapping';

interface GalleryFolderMapperProps {
  folderName: string;
  suggestion: FolderMappingSuggestion;
  selectedUnitIds: string[];
  units: PropertyUnit[];
  onSelectionChange: (unitIds: string[]) => void;
  imageCount?: number;
}

export function GalleryFolderMapper({
  folderName,
  suggestion,
  selectedUnitIds,
  units,
  onSelectionChange,
  imageCount = 0,
}: GalleryFolderMapperProps) {
  const [open, setOpen] = useState(false);

  const normalized = normalizeFolderNameForStorage(folderName);
  const selectedUnits = units.filter((u) => selectedUnitIds.includes(u.id));
  const hasSuggestion = suggestion.suggestedUnitIds.length > 0;
  const isMapped = selectedUnitIds.length > 0;

  const toggleUnit = (unit: PropertyUnit) => {
    const id = unit.id;
    if (selectedUnitIds.includes(id)) {
      onSelectionChange(selectedUnitIds.filter((x) => x !== id));
    } else {
      onSelectionChange([...selectedUnitIds, id].sort());
    }
  };

  const applySuggestion = () => {
    if (hasSuggestion) {
      onSelectionChange([...suggestion.suggestedUnitIds].sort());
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-lg border p-3',
        isMapped ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/20'
      )}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="font-medium truncate" title={folderName}>
            {folderName}
          </p>
          <p className="text-xs text-muted-foreground">
            Storage: <code className="rounded bg-muted px-1">{normalized}</code>
            {imageCount > 0 && (
              <>
                <span className="mx-1">·</span>
                {imageCount} image(s)
              </>
            )}
          </p>
        </div>
      </div>

      {hasSuggestion && (
        <div className="flex items-center gap-1">
          <Badge
            variant={suggestion.confidence === 'high' ? 'default' : 'secondary'}
            className="text-xs"
          >
            {suggestion.confidence} match
          </Badge>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={applySuggestion}
            className="h-7 text-xs"
          >
            Apply
          </Button>
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-w-[180px] justify-between"
          >
            <span className="truncate">
              {selectedUnits.length === 0
                ? 'Select units'
                : selectedUnits.length === 1
                  ? selectedUnits[0].unit_number
                  : `${selectedUnits.length} units`}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="end">
          <Command>
            <CommandInput placeholder="Search unit..." />
            <CommandEmpty>No unit found.</CommandEmpty>
            <CommandGroup className="max-h-[240px] overflow-auto">
              {units.map((unit) => {
                const selected = selectedUnitIds.includes(unit.id);
                return (
                  <CommandItem
                    key={unit.id}
                    value={`${unit.unit_number} ${unit.id}`}
                    onSelect={() => toggleUnit(unit)}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded border',
                        selected ? 'bg-primary text-primary-foreground' : 'border-input'
                      )}
                    >
                      {selected ? <Check className="h-3 w-3" /> : null}
                    </div>
                    <span>
                      {unit.unit_number}
                      <span className="ml-1 text-muted-foreground text-xs">
                        {unit.unit_type}
                      </span>
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {isMapped && (
        <Badge variant="outline" className="shrink-0">
          <Link2 className="mr-1 h-3 w-3" />
          Mapped
        </Badge>
      )}
    </div>
  );
}
